import {
  ChangeOrder,
  DealPLOverrides,
  EnhancedDeal,
  GlobalCosts,
  Payment,
  ProjectPhase,
  ProjectPLBucket,
  ProposalBuilderData,
  PROJECT_PL_BUCKETS,
  QBExpense,
} from '../types';
import { getProjectDisplayName } from './projectName';
import { activePlReconciliationLines, sumActivePlReconciliations, sumContractPlReconciliations } from './plReconciliation';
import { computeChangeOrderBudgetByBucket, isApprovedChangeOrder } from './contractValue';
import { isStructureClientSelected } from './proposalPublicGrandTotals';
import type { PurchaseJobPlBucket, PurchaseRequisition } from '../types/purchasing';
import { isMultiSupplierIssuedPurchase, splitSupplierPurchaseCommitment } from './purchaseOrderPurchases';
import {
  committedJobPurchases,
  purchaseCommitmentKind,
  resolvePurchaseDealId,
  resolvePurchasePlBucket,
} from './purchaseBudget';
import { isPurchaseJobPlBucket, resolveSpendClass } from './purchasingSpendClass';
import type { DesignerInvoiceJobCash } from './designerInvoicePL';
import {
  joinProjectPlExpensesToPos,
  poQbStatusLabel,
  type ProjectPlMatchPo,
  type ProjectPlPoMatchSource,
} from './projectPlPoQbMatch';

export type DesignFeePaymentLite = {
  amount: number;
  paidAt: Date | string | undefined;
  dealId?: string;
  portalId?: string;
  clientName?: string;
};

export type LinkedPaymentLite = Payment & { id: string };
export type LinkedExpenseLite = QBExpense & { id: string };

/**
 * Resolve the amount of a transaction that should be credited to a specific
 * deal. Returns `null` when the transaction doesn't touch this deal at all.
 *
 * - When the transaction is split, we use the split slice for this deal (if any).
 * - When not split, we fall back to the auto-/PM-matched deal id.
 */
function txnAllocationForDeal(
  txn: {
    matchedDealId?: string;
    splits?: { dealId: string | null; amount: number; note?: string; projectPhase?: ProjectPhase }[];
  },
  amount: number,
  dealId: string,
): { amount: number; isSplit: boolean; splitNote?: string; projectPhase?: ProjectPhase } | null {
  if (txn.splits && txn.splits.length > 0) {
    const slice = txn.splits.find(s => s.dealId === dealId);
    if (!slice) return null;
    return {
      amount: Number(slice.amount) || 0,
      isSplit: true,
      splitNote: slice.note,
      projectPhase: slice.projectPhase,
    };
  }
  if (txn.matchedDealId && txn.matchedDealId === dealId) {
    return { amount, isSplit: false };
  }
  return null;
}

type ProjectPLDirectExpensePick = Pick<
  QBExpense,
  | 'excluded'
  | 'categorizationStatus'
  | 'matchedDealIdOverridden'
  | 'category'
  | 'totalAmount'
  | 'matchedDealId'
  | 'splits'
>;

/** True when a QB expense row should count toward this deal's direct P&L cost pool. */
export function projectPLDirectExpenseIncluded(
  expense: ProjectPLDirectExpensePick,
  dealId: string,
): boolean {
  if (expense.excluded) return false;
  if (expense.categorizationStatus === 'pending' && !expense.matchedDealIdOverridden) return false;
  if (expense.category === 'overhead') return false;
  const total = Number(expense.totalAmount) || 0;
  const alloc = txnAllocationForDeal(expense, total, dealId);
  if (!alloc) return false;
  if (expense.category === 'unmatched' && !alloc.isSplit) {
    const explicitlyLinked =
      expense.categorizationStatus === 'categorized' || Boolean(expense.matchedDealId);
    if (!explicitlyLinked) return false;
  }
  return true;
}

/** Whether an expense might allocate to this deal (single-match or split slice). */
export function projectPLExpenseTouchesDeal(
  expense: Pick<QBExpense, 'matchedDealId' | 'splits'>,
  dealId: string,
): boolean {
  if (expense.splits && expense.splits.length > 0) {
    return expense.splits.some(s => s.dealId === dealId);
  }
  return expense.matchedDealId === dealId;
}

/** Resolved P&L bucket — same precedence as aggregateProjectPL expense rows. */
export function resolveProjectPLExpenseBucket(
  expense: Pick<QBExpense, 'id' | 'plBucket' | 'accountName' | 'description' | 'category'>,
  overrides?: DealPLOverrides | null,
): ProjectPLBucket {
  const auto = mapQbExpenseToBucket(expense);
  const plBucketOverride =
    typeof expense.plBucket === 'string' && expense.plBucket.trim()
      ? (expense.plBucket.trim() as ProjectPLBucket)
      : null;
  const overrideBucket = expense.id ? overrides?.expenseBuckets?.[expense.id] : undefined;
  return plBucketOverride || overrideBucket || auto;
}

/**
 * QB + designer-invoice actual for one job purchase bucket — matches Project P&L bucket actuals.
 */
export function sumPurchaseJobBucketPlActual(input: {
  expenses: Array<QBExpense & { id?: string }>;
  dealId: string;
  bucket: PurchaseJobPlBucket;
  overrides?: DealPLOverrides | null;
  designerInvoiceJobCash?: DesignerInvoiceJobCash[];
}): number {
  let total = 0;
  for (const expense of input.expenses) {
    if (!projectPLDirectExpenseIncluded(expense, input.dealId)) continue;
    if (resolveProjectPLExpenseBucket(expense, input.overrides) !== input.bucket) continue;
    const alloc = txnAllocationForDeal(expense, Number(expense.totalAmount) || 0, input.dealId);
    total += alloc?.amount || 0;
  }
  for (const row of input.designerInvoiceJobCash || []) {
    if (row.dealId !== input.dealId) continue;
    if (row.plBucket !== input.bucket) continue;
    total += row.amount;
  }
  return total;
}

/** True when a transaction has been allocated (matched or split). */
function txnIsAllocated(txn: { matchedDealId?: string; splits?: { dealId: string | null }[] }): boolean {
  if (txn.splits && txn.splits.length > 0) return true;
  return Boolean(txn.matchedDealId);
}
void txnIsAllocated;

/** Per-bucket roll-up shown in the row drill-down. */
export interface ProjectPLCommittedLine {
  id: string;
  label: string;
  amount: number;
  vendorName?: string;
  title?: string;
}

export interface ProjectPLBucketRow {
  bucket: ProjectPLBucket;
  label: string;
  budget: number;
  committed: number;
  committedLines: ProjectPLCommittedLine[];
  actual: number;
  variance: number;        // actual − budget; positive = over budget
  variancePct: number;     // variance / budget * 100 (0 when budget = 0)
}

export interface ProjectPLExpenseRow {
  id: string;
  vendorName: string;
  description?: string;
  txnDate: string;
  /** Amount credited to THIS deal — split slice when split, full total otherwise. */
  amount: number;
  /** Original transaction total (only differs from `amount` when split). */
  totalAmount: number;
  /** Auto-mapped bucket (account substring or category fallback). */
  autoBucket: ProjectPLBucket;
  /** PM override stored in `dealPLOverrides`. Falls back to autoBucket. */
  bucket: ProjectPLBucket;
  category: 'direct' | 'overhead' | 'unmatched';
  matchedDealId?: string;
  customerName?: string;
  hasOverride: boolean;
  accountName: string;
  /** Bank / credit-card account the funds came from (Purchase.AccountRef.name). */
  bankAccountName?: string;
  /** True when this row only counts a slice of the source transaction. */
  isSplit: boolean;
  splitNote?: string;
  /** Phase tag when this row comes from a split slice (precon vs under contract). */
  projectPhase?: ProjectPhase;
  purchaseRequisitionId?: string;
  poNumber?: string;
  poMatchSource?: Exclude<ProjectPlPoMatchSource, 'none'>;
  quickbooksId?: string;
  entityType?: QBExpense['entityType'];
}

export type ProjectPlPoQbStatus = 'linked_expense' | 'linked_bill' | 'pending_bill' | 'none';

export interface ProjectPLPoRow {
  id: string;
  label: string;
  vendorName?: string;
  title?: string;
  amount: number;
  bucketLabel: string;
  txnDate?: string;
  qbStatus: ProjectPlPoQbStatus;
  qbLabel: string;
  matchedExpenseIds: string[];
  qbBillStatus?: string;
  qbBillId?: string;
  poMatchSource: ProjectPlPoMatchSource;
}

export interface ProjectPLPaymentRow {
  id: string;
  source: 'quickbooks' | 'design-fee';
  description: string;
  /** Amount credited to THIS deal — split slice when split, full total otherwise. */
  amount: number;
  /** Original transaction total (only differs from `amount` when split). */
  totalAmount: number;
  paidAt: Date | null;
  excluded: boolean;
  /** Bank account the payment was deposited into (DepositToAccountRef.name). */
  bankAccountName?: string;
  /** True when this row only counts a slice of the source transaction. */
  isSplit: boolean;
  splitNote?: string;
}

export interface ProjectCashFlowPoint {
  /** Calendar month bucket, YYYY-MM. */
  periodKey: string;
  label: string;
  moneyIn: number;
  moneyOut: number;
  net: number;
  cumulativeIn: number;
  cumulativeOut: number;
  cumulativeNet: number;
}

function parseProjectTxnDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'string') {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

/** Monthly money in (payments) vs money out (expenses) for a single project. */
export function buildProjectCashFlowTimeline(
  payments: Pick<ProjectPLPaymentRow, 'amount' | 'paidAt' | 'excluded'>[],
  expenses: Pick<ProjectPLExpenseRow, 'amount' | 'txnDate'>[],
): ProjectCashFlowPoint[] {
  const byMonth = new Map<string, { moneyIn: number; moneyOut: number }>();

  for (const payment of payments) {
    if (payment.excluded || !payment.paidAt) continue;
    const key = toMonthKey(payment.paidAt);
    const cur = byMonth.get(key) || { moneyIn: 0, moneyOut: 0 };
    cur.moneyIn += payment.amount;
    byMonth.set(key, cur);
  }

  for (const expense of expenses) {
    const date = parseProjectTxnDate(expense.txnDate);
    if (!date) continue;
    const key = toMonthKey(date);
    const cur = byMonth.get(key) || { moneyIn: 0, moneyOut: 0 };
    cur.moneyOut += expense.amount;
    byMonth.set(key, cur);
  }

  let cumulativeIn = 0;
  let cumulativeOut = 0;

  return Array.from(byMonth.keys())
    .sort()
    .map(periodKey => {
      const { moneyIn, moneyOut } = byMonth.get(periodKey)!;
      cumulativeIn += moneyIn;
      cumulativeOut += moneyOut;
      const [year, month] = periodKey.split('-').map(Number);
      const monthDate = new Date(year, month - 1, 1);
      return {
        periodKey,
        label: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        moneyIn,
        moneyOut,
        net: moneyIn - moneyOut,
        cumulativeIn,
        cumulativeOut,
        cumulativeNet: cumulativeIn - cumulativeOut,
      };
    });
}

function toMonthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** `contract` = deal value (+ design fees); `collected` = QuickBooks / Stripe cash actually received. */
export type ProjectPLRevenueSource = 'contract' | 'collected';

/** Marker shown next to revenue that is cash collected rather than a contract value. */
export const PL_COLLECTED_REVENUE_MARK = '*';
export const PL_COLLECTED_REVENUE_NOTE =
  '* Collected revenue — this job predates the CRM (no proposal or deal value), so revenue is the cash actually received.';

export interface ProjectPLRow {
  dealId: string;
  dealTitle: string;
  contactName: string;
  pipelineStageId: string;
  pipelineStageName?: string;

  // Revenue: signed deal value + Stripe design fee paid for this project.
  // Pre-CRM jobs with no proposal and no deal value fall back to cash collected
  // (`revenueSource: 'collected'`) so they never read as $0.
  revenue: number;
  revenueSource: ProjectPLRevenueSource;
  dealValue: number;
  designFeeRevenue: number;
  /** Sum of active ghost P&L adjustments (not part of the CRM deal value). */
  plReconciliationTotal: number;
  /** Ghost dollars included in the Progress Billing contract. */
  plReconciliationContractTotal: number;
  plReconciliations: { id: string; amount: number; title: string; reason: string }[];

  // Cost: direct expenses + revenue-share allocation of overhead pool.
  directCost: number;
  allocatedOverhead: number;
  actualCost: number;

  // Budget from latest proposal (sum of bucket budgets) plus approved change orders.
  budget: number;
  // Net budget contributed by approved change orders (negative for credits);
  // 0 when no approved CO touches the budget.
  changeOrderBudget: number;

  // Margin: revenue − actual cost.
  grossMargin: number;
  grossMarginPct: number;

  // Variance vs budget (actualCost vs budget).
  varianceVsBudget: number;
  varianceVsBudgetPct: number;

  // Per-bucket budget vs actual.
  buckets: ProjectPLBucketRow[];

  // Drill-in details.
  expenses: ProjectPLExpenseRow[];
  payments: ProjectPLPaymentRow[];
  pos: ProjectPLPoRow[];

  // Status flags.
  unassignedExpenseCount: number;
  hasProposal: boolean;
  cashCollected: number;
  readyToFinalize: boolean;

  // PM has flagged this row as excluded — drop it from KPIs and overhead allocation.
  excluded: boolean;
  excludedReason?: string;

  // Finalization metadata (mirrors EnhancedDeal).
  pmFinalizedAt?: Date;
  pmFinalizedBy?: string;
}

/** Friendly labels (used by tables, tooltips, dropdowns). */
export const PROJECT_PL_BUCKET_LABELS: Record<ProjectPLBucket, string> = {
  design: 'Design',
  engineering: 'Engineering',
  timber: 'Timber & materials',
  production: 'Production',
  freight: 'Freight',
  install: 'Install',
  travel: 'Travel',
  overhead: 'Overhead',
  unassigned: 'Unassigned',
};

/** Short description shown next to the bucket dropdown / tooltip. */
export const PROJECT_PL_BUCKET_HINTS: Record<ProjectPLBucket, string> = {
  design: 'Design hours scoped on the proposal (CAD / drawings).',
  engineering: 'External engineering (PE stamps, calcs) on the job.',
  timber: 'Raw timber, decking, and material kit line items.',
  production: 'CNC + shop labor that turns timber into the kit.',
  freight: 'Inbound + outbound freight.',
  install: 'On-site install crew, equipment rental, install overhead.',
  travel: 'Hotels, per diem, fuel, and travel days.',
  overhead: 'Allocated company overhead share + un-categorized indirect costs.',
  unassigned: 'No bucket yet — pick one to fold into the right budget line.',
};

/** Default account-name substring → bucket mapping (case-insensitive substring match). */
const DEFAULT_ACCOUNT_BUCKET_MAP: Array<{ match: RegExp; bucket: ProjectPLBucket }> = [
  // Engineering before design: "Engineering Services", PE stamps, structural calcs are their own P&L line.
  { match: /engineering|engineer\b|\bPE\b|stamp|structural calc/i, bucket: 'engineering' },
  { match: /design|drafting|architect/i, bucket: 'design' },
  { match: /timber|lumber|wood|material|decking|fastener|peg|hardware/i, bucket: 'timber' },
  { match: /cnc|shop|production|fabricat/i, bucket: 'production' },
  { match: /freight|shipping|delivery|trucking/i, bucket: 'freight' },
  { match: /install|crane|telehandler|scissor|lift|labor/i, bucket: 'install' },
  { match: /travel|hotel|lodging|per\s?diem|airfare|fuel|gas|mileage/i, bucket: 'travel' },
  { match: /overhead|insurance|admin|office|utilities|software|subscription/i, bucket: 'overhead' },
];

/**
 * Map a single QB expense to its default bucket.
 *
 * Order:
 * 1. Account-name regex match (most specific signal).
 * 2. Description regex match (fallback).
 * 3. `category` fallback: 'overhead' → overhead, 'unmatched' → unassigned, 'direct' → unassigned.
 */
export function mapQbExpenseToBucket(expense: {
  accountName?: string;
  description?: string;
  category?: QBExpense['category'];
}): ProjectPLBucket {
  const accountName = expense.accountName || '';
  for (const { match, bucket } of DEFAULT_ACCOUNT_BUCKET_MAP) {
    if (match.test(accountName)) return bucket;
  }
  const desc = expense.description || '';
  if (desc) {
    for (const { match, bucket } of DEFAULT_ACCOUNT_BUCKET_MAP) {
      if (match.test(desc)) return bucket;
    }
  }
  if (expense.category === 'overhead') return 'overhead';
  return 'unassigned';
}

function sumGlobalCostItems(globalCosts: GlobalCosts | undefined, keys: Array<keyof GlobalCosts>): number {
  if (!globalCosts) return 0;
  return keys.reduce((s, key) => {
    const item = (globalCosts as any)[key];
    if (!item || typeof item !== 'object') return s;
    if (item.enabled === false) return s;
    const amount = Number(item.amount);
    return s + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

/**
 * Compute the per-bucket budget from the latest proposal builder doc.
 *
 * Budgets are RAW (pre-margin) costs — we want to compare apples to apples
 * against actual QB expense outflows. Disabled (excluded) structures are
 * ignored, mirroring `nodeSetsIncludedInProposal`.
 *
 * Phased proposals: the budget respects the `structureSelections` snapshot
 * taken at initial signing, so structures in uncommitted phases are excluded.
 * Later-phase commitments arrive via approved change orders, whose budgets are
 * folded in separately (`computeChangeOrderBudgetByBucket`) — including them
 * here would double count.
 */
export function getProposalBucketBudget(
  proposal: ProposalBuilderData | null | undefined,
): Record<ProjectPLBucket, number> {
  const empty: Record<ProjectPLBucket, number> = {
    design: 0,
    engineering: 0,
    timber: 0,
    production: 0,
    freight: 0,
    install: 0,
    travel: 0,
    overhead: 0,
    unassigned: 0,
  };
  if (!proposal) return empty;

  const phased = !!proposal.phasingEnabled && (proposal.phases?.length || 0) > 0;
  const enabled = (proposal.nodeSets || []).filter(ns =>
    isStructureClientSelected(ns, proposal.structureSelections, phased),
  );

  enabled.forEach(ns => {
    const qty = ns.quantity || 1;
    empty.design += (ns.design?.total || 0) * qty;
    empty.timber += (ns.materials?.total || 0) * qty;
    empty.production += (ns.production?.total || 0) * qty;
    empty.freight += (ns.freight?.total || 0) * qty;
    // Install bucket = install + overhead node + install-line overhead.
    empty.install +=
      ((ns.install?.total || 0) +
        (ns.overhead?.total || 0) +
        (ns.install?.overheadTotal || 0)) *
      qty;
    empty.travel += (ns.travel?.total || 0) * qty;
  });

  // Fold global-cost line items into the right buckets so PM Budget vs Actual
  // lines up with how the proposal actually scoped freight / travel / overhead.
  const gc = proposal.globalCosts;
  empty.freight += sumGlobalCostItems(gc, ['globalFreightInbound', 'globalFreightOutbound']);
  empty.travel += sumGlobalCostItems(gc, ['baselineTravel', 'additionalTravel']);
  empty.overhead += sumGlobalCostItems(gc, [
    'equipmentRental',
    'additionalGear',
    'ppeBudget',
    'jobsiteIncidentals',
    'additionalInsurance',
    'damagedTimberBudget',
    'jobsiteSecurity',
    'additionalVehicles',
    'jobSpecificTooling',
  ]);
  empty.overhead += proposal.projectOverheadAmount || 0;

  return empty;
}

export interface AggregateProjectPLInput {
  deal: EnhancedDeal;
  proposal: ProposalBuilderData | null | undefined;
  /** Approved change orders feed the bucket budget (revenue is already in deal.value). */
  changeOrders?: ChangeOrder[];
  expenses: LinkedExpenseLite[];                  // ALL synced expenses; the aggregator filters direct vs overhead-share.
  payments: LinkedPaymentLite[];                  // ALL QB payments; aggregator filters by matchedDealId + excluded.
  designFeePayments: DesignFeePaymentLite[];      // Stripe design fee receipts (already keyed by dealId).
  overrides?: DealPLOverrides | null;
  /** Total revenue across all won deals — used for revenue-share overhead allocation. */
  totalWonRevenue: number;
  /** Total overhead-pool spend across the company in the same time window. */
  totalOverheadPool: number;
  /** Optional pipeline stage label resolution (parent passes `stageName`). */
  pipelineStageName?: string;
  /** Open job POs — shown as Committed, never added to actual. Purchased or QB-linked POs are omitted. */
  jobPurchases?: PurchaseRequisition[];
  /** Approved designer-invoice job-direct cash (non-overhead lines). */
  designerInvoiceJobCash?: DesignerInvoiceJobCash[];
}

function requisitionAmount(row: PurchaseRequisition): number {
  if (row.status === 'purchased' && Number.isFinite(row.purchasedAmount)) return row.purchasedAmount as number;
  if (isMultiSupplierIssuedPurchase(row)) return splitSupplierPurchaseCommitment(row).open;
  return Number.isFinite(row.amount) ? row.amount : 0;
}

function toProjectPlMatchPo(row: PurchaseRequisition): ProjectPlMatchPo {
  return {
    id: row.id,
    poNumber: row.poNumber,
    prNumber: row.prNumber,
    status: row.status,
    vendorName: row.vendorName,
    title: row.title,
    amount: row.amount,
    purchasedAmount: row.purchasedAmount,
    paymentMethodId: row.paymentMethodId,
    paymentMethod: row.paymentMethod,
    spendClass: row.spendClass,
    dealId: row.dealId,
    plBucket: row.plBucket,
    costObject: row.costObject,
    purchasedAt: row.purchasedAt,
    approvedAt: row.approvedAt,
    createdAt: row.createdAt,
    qbExpenseId: row.qbExpenseId,
    qbExpenseIds: row.qbExpenseIds,
    qbBillStatus: row.qbBillStatus,
    qbBillId: row.qbBillId,
    designerInvoiceId: row.designerInvoiceId,
  };
}

function dealCommittedJobPurchases(rows: PurchaseRequisition[] | undefined, dealId: string): PurchaseRequisition[] {
  return (rows || []).filter(row => {
    if (purchaseCommitmentKind(row.status) !== 'committed') return false;
    if (resolveSpendClass(row.costObject) !== 'job') return false;
    return resolvePurchaseDealId(row) === dealId;
  });
}

/** Purchased POs are closed. A QB bill or card charge means the spend is already an actual. */
function projectPlPoIsOpen(
  po: PurchaseRequisition,
  match: { source: ProjectPlPoMatchSource } | undefined,
): boolean {
  if (po.status !== 'approved') return false;
  if ((po.qbBillStatus || '').trim() === 'linked') return false;
  if ((po.qbExpenseId || '').trim()) return false;
  if ((po.qbExpenseIds || []).some(id => (id || '').trim())) return false;
  const source = match?.source || 'none';
  return source !== 'expense' && source !== 'bill' && source !== 'heuristic';
}

function toDate(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === 'function') {
    try {
      return v.toDate();
    } catch {
      return null;
    }
  }
  const t = typeof v === 'string' || typeof v === 'number' ? new Date(v) : null;
  return t && !Number.isNaN(t.getTime()) ? t : null;
}

/**
 * Build a single Project P&L row. Pure / synchronous so the dashboard memo
 * can recompute on every state change without ordering hazards.
 */
export function aggregateProjectPL(input: AggregateProjectPLInput): ProjectPLRow {
  const {
    deal,
    proposal,
    expenses,
    payments,
    designFeePayments,
    overrides,
    totalWonRevenue,
    totalOverheadPool,
    pipelineStageName,
  } = input;

  const dealId = deal.id || '';
  const dealValue = Number(deal.value) || 0;
  const excluded = Boolean(overrides?.excluded);

  // Revenue ─ deal value + Stripe design fees paid for this dealId.
  const designFeeRows = designFeePayments.filter(p => p.dealId && p.dealId === dealId);
  const designFeeRevenue = designFeeRows.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const plReconciliations = activePlReconciliationLines(overrides?.plReconciliations);
  const plReconciliationTotal = sumActivePlReconciliations(overrides?.plReconciliations);
  const plReconciliationContractTotal = sumContractPlReconciliations(overrides?.plReconciliations, Boolean(proposal));
  const contractRevenue = dealValue + designFeeRevenue + plReconciliationTotal;

  // Direct expenses touching this deal — either via single-match (matchedDealId)
  // or via a split slice that targets this deal.
  const directExpenses = expenses.filter(e => projectPLDirectExpenseIncluded(e, dealId));
  const directCost = directExpenses.reduce((s, e) => {
    const total = Number(e.totalAmount) || 0;
    const alloc = txnAllocationForDeal(e, total, dealId);
    return s + (alloc?.amount || 0);
  }, 0);
  const designerCash = (input.designerInvoiceJobCash || []).filter(c => c.dealId === dealId);
  const designerCost = designerCash.reduce((s, c) => s + c.amount, 0);

  // Excluded projects skip overhead allocation entirely. The remaining active
  // projects share the full overhead pool (the batch helper recomputes the
  // revenue-share denominator without the excluded rows).
  const revenueShare = totalWonRevenue > 0 ? dealValue / totalWonRevenue : 0;
  const allocatedOverhead = excluded ? 0 : totalOverheadPool * revenueShare;
  const actualCost = directCost + designerCost + allocatedOverhead;

  // Budget per bucket (from proposal). Overhead bucket gets the same revenue
  // share of the company-wide overhead pool so per-bucket variance is comparable.
  const budgetMap = getProposalBucketBudget(proposal);
  // Fold approved change-order costs into the bucket budget so Budget-vs-Actual
  // stays honest once the contract value grows/shrinks via change orders.
  // Sign-preserving: a lump-sum credit lowers the bucket budget.
  let changeOrderBudget = 0;
  (input.changeOrders || []).forEach(co => {
    if (!isApprovedChangeOrder(co)) return;
    const byBucket = computeChangeOrderBudgetByBucket(co);
    (Object.keys(byBucket) as ProjectPLBucket[]).forEach(b => {
      const amount = byBucket[b] || 0;
      budgetMap[b] = (budgetMap[b] || 0) + amount;
      changeOrderBudget += amount;
    });
  });
  // The proposal's `overhead` bucket already covers global cost overhead +
  // project-overhead %, but we want the ACTUAL allocated overhead for variance,
  // not the proposal's modeled overhead. We keep the proposal's number as the
  // overhead BUDGET — variance answers "did we spend more overhead than we modeled?".

  // Bucket actuals — direct expenses bucket-mapped + allocated overhead → 'overhead'.
  const expenseRows: ProjectPLExpenseRow[] = directExpenses
    .map(e => {
      const auto = mapQbExpenseToBucket(e);
      const plBucketOverride =
        typeof e.plBucket === 'string' && e.plBucket.trim()
          ? (e.plBucket.trim() as ProjectPLBucket)
          : null;
      const overrideBucket = overrides?.expenseBuckets?.[e.id];
      const bucket = resolveProjectPLExpenseBucket(e, overrides);
      const total = Number(e.totalAmount) || 0;
      const alloc = txnAllocationForDeal(e, total, dealId);
      const paid: ProjectPLExpenseRow = {
        id: e.id,
        vendorName: e.vendorName || 'Vendor',
        description: e.description,
        txnDate: e.txnDate,
        amount: alloc?.amount || 0,
        totalAmount: total,
        autoBucket: auto,
        bucket,
        category: e.category,
        matchedDealId: e.matchedDealId,
        customerName: e.customerName,
        hasOverride: Boolean(overrideBucket || plBucketOverride),
        accountName: e.accountName || '',
        bankAccountName: e.bankAccountName,
        isSplit: Boolean(alloc?.isSplit),
        splitNote: alloc?.splitNote,
        projectPhase: alloc?.projectPhase,
        purchaseRequisitionId: e.purchaseRequisitionId,
        poNumber: e.poNumber,
        quickbooksId: e.quickbooksId,
        entityType: e.entityType,
      };
      return paid;
    });

  for (const c of designerCash) {
    expenseRows.push({
      id: c.id,
      vendorName: c.vendor,
      description: `${c.description} · ${c.invoiceNumber}`.trim(),
      txnDate: c.txnDate,
      amount: c.amount,
      totalAmount: c.amount,
      autoBucket: c.plBucket,
      bucket: c.plBucket,
      category: 'direct',
      hasOverride: true,
      accountName: 'Designer invoice (approved)',
      bankAccountName: undefined,
      isSplit: false,
    });
  }
  expenseRows.sort((a, b) => (a.txnDate < b.txnDate ? 1 : -1));

  const dealPos = dealCommittedJobPurchases(input.jobPurchases, dealId);
  const join = joinProjectPlExpensesToPos(
    expenseRows
      .filter(row => !row.id.startsWith('designer-invoice:'))
      .map(row => ({
        id: row.id,
        vendorName: row.vendorName,
        description: row.description,
        totalAmount: row.totalAmount,
        txnDate: row.txnDate,
        purchaseRequisitionId: row.purchaseRequisitionId,
        poNumber: row.poNumber,
        quickbooksId: row.quickbooksId,
        entityType: row.entityType,
        matchedDealId: row.matchedDealId,
        splits: directExpenses.find(e => e.id === row.id)?.splits?.map(slice => ({
          dealId: slice.dealId,
          amount: slice.amount,
        })),
      })),
    dealPos.map(toProjectPlMatchPo),
  );
  for (const row of expenseRows) {
    const match = join.expenseById[row.id];
    if (!match) continue;
    row.purchaseRequisitionId = match.poId;
    row.poNumber = match.poNumber;
    row.poMatchSource = match.source;
  }
  const openDealPos = dealPos.filter(po => {
    if (!projectPlPoIsOpen(po, join.poById[po.id])) return false;
    if (isMultiSupplierIssuedPurchase(po) && splitSupplierPurchaseCommitment(po).open <= 0) return false;
    return true;
  });
  const openPoIds = new Set(openDealPos.map(po => po.id));
  const pos: ProjectPLPoRow[] = openDealPos
    .map(po => {
      const match = join.poById[po.id] || {
        expenseIds: [] as string[],
        source: 'none' as const,
        qbBillStatus: po.qbBillStatus,
      };
      const status = poQbStatusLabel({ source: match.source, qbBillStatus: po.qbBillStatus });
      const bucket = resolvePurchasePlBucket(po);
      const date = po.purchasedAt || po.approvedAt || po.createdAt;
      return {
        id: po.id,
        label: po.poNumber || po.prNumber,
        vendorName: po.vendorName,
        title: po.title,
        amount: requisitionAmount(po),
        bucketLabel: bucket ? PROJECT_PL_BUCKET_LABELS[bucket] : 'Unassigned',
        txnDate: date ? date.toISOString().slice(0, 10) : undefined,
        qbStatus: status.qbStatus,
        qbLabel: status.qbLabel,
        matchedExpenseIds: match.expenseIds,
        qbBillStatus: po.qbBillStatus,
        qbBillId: po.qbBillId,
        poMatchSource: match.source,
      };
    })
    .sort((a, b) => b.amount - a.amount || a.label.localeCompare(b.label));

  const actualMap: Record<ProjectPLBucket, number> = {
    design: 0,
    engineering: 0,
    timber: 0,
    production: 0,
    freight: 0,
    install: 0,
    travel: 0,
    overhead: 0,
    unassigned: 0,
  };
  expenseRows.forEach(r => {
    actualMap[r.bucket] += r.amount;
  });
  // Fold the company overhead share into the overhead bucket for budget vs actual.
  actualMap.overhead += allocatedOverhead;

  const buckets: ProjectPLBucketRow[] = PROJECT_PL_BUCKETS.map(bucket => {
    const budget = budgetMap[bucket] || 0;
    const actual = actualMap[bucket] || 0;
    const committedLines = isPurchaseJobPlBucket(bucket)
      ? committedJobPurchases({
          rows: (input.jobPurchases || []).filter(row => openPoIds.has(row.id)),
          dealId,
          plBucket: bucket,
        })
      : [];
    const committed = committedLines.reduce((sum, line) => sum + line.amount, 0);
    const variance = actual - budget;
    const variancePct = budget > 0 ? (variance / budget) * 100 : actual > 0 ? 100 : 0;
    return {
      bucket,
      label: PROJECT_PL_BUCKET_LABELS[bucket],
      budget,
      committed,
      committedLines,
      actual,
      variance,
      variancePct,
    };
  });

  const budget = buckets.reduce((s, b) => s + b.budget, 0);
  const varianceVsBudget = actualCost - budget;
  const varianceVsBudgetPct = budget > 0 ? (varianceVsBudget / budget) * 100 : actualCost > 0 ? 100 : 0;

  // Linked payment rows (QB payments matched to this deal + Stripe design fee receipts).
  // Honors split allocations so a single deposit can be partially credited.
  const qbPayments = payments
    .filter(p => {
      const total = Number(p.amount) || 0;
      return Boolean(txnAllocationForDeal(p, total, dealId));
    })
    .map<ProjectPLPaymentRow>(p => {
      const total = Number(p.amount) || 0;
      const alloc = txnAllocationForDeal(p, total, dealId);
      return {
        id: p.id,
        source: 'quickbooks',
        description: p.description || p.matchedDealTitle || 'Payment',
        amount: alloc?.amount || 0,
        totalAmount: total,
        paidAt: toDate(p.paidAt) || toDate(p.paymentDate),
        excluded: Boolean(p.excluded),
        bankAccountName: p.bankAccountName,
        isSplit: Boolean(alloc?.isSplit),
        splitNote: alloc?.splitNote,
      };
    });

  const designFeeRowsAsPayments: ProjectPLPaymentRow[] = designFeeRows.map(p => {
    const total = Number(p.amount) || 0;
    return {
      id: `${p.portalId || 'design'}-${p.dealId}`,
      source: 'design-fee',
      description: `Stripe design fee · ${p.clientName || ''}`.trim(),
      amount: total,
      totalAmount: total,
      paidAt: toDate(p.paidAt),
      excluded: false,
      isSplit: false,
    };
  });

  const allPayments = [...qbPayments, ...designFeeRowsAsPayments].sort((a, b) => {
    const aT = a.paidAt ? a.paidAt.getTime() : 0;
    const bT = b.paidAt ? b.paidAt.getTime() : 0;
    return bT - aT;
  });

  const cashCollected = allPayments
    .filter(p => !p.excluded)
    .reduce((s, p) => s + p.amount, 0);

  // Jobs that predate the CRM have no proposal and no deal value. Their
  // revenue is what the client actually paid; the UI marks it with `*`.
  const revenueSource: ProjectPLRevenueSource =
    !proposal && dealValue === 0 && cashCollected > 0 ? 'collected' : 'contract';
  // No proposal: the ghost true-up is part of project value, including the cash-collected fallback.
  const revenue = revenueSource === 'collected' ? cashCollected + plReconciliationTotal : contractRevenue;
  const grossMargin = revenue - actualCost;
  const grossMarginPct = revenue > 0 ? (grossMargin / revenue) * 100 : 0;

  const unassignedExpenseCount = expenseRows.filter(r => r.bucket === 'unassigned').length;

  // "Ready to finalize" advisory: enough cash to cover the contract AND no unassigned buckets.
  // Collected-revenue rows have no contract to cover, so they never auto-flag.
  const readyToFinalize =
    !deal.pmFinalizedAt &&
    contractRevenue > 0 &&
    cashCollected >= contractRevenue * 0.95 &&
    unassignedExpenseCount === 0;

  return {
    dealId,
    dealTitle: getProjectDisplayName(deal) || 'Untitled deal',
    contactName: deal.contactName || '',
    pipelineStageId: deal.stageId,
    pipelineStageName,

    revenue,
    revenueSource,
    dealValue,
    designFeeRevenue,
    plReconciliationTotal,
    plReconciliationContractTotal,
    plReconciliations,

    directCost: directCost + designerCost,
    allocatedOverhead,
    actualCost,

    budget,
    changeOrderBudget,

    grossMargin,
    grossMarginPct,

    varianceVsBudget,
    varianceVsBudgetPct,

    buckets,
    expenses: expenseRows,
    payments: allPayments,
    pos,

    unassignedExpenseCount,
    hasProposal: Boolean(proposal),
    cashCollected,
    readyToFinalize,

    excluded,
    excludedReason: overrides?.excludedReason,

    pmFinalizedAt: deal.pmFinalizedAt,
    pmFinalizedBy: deal.pmFinalizedBy,
  };
}

/**
 * Convenience: build P&L rows for many deals using a shared expense pool.
 *
 * `proposalsByDeal` is a `dealId → ProposalBuilderData` map populated by the
 * dashboard component (subscribed via `proposalBuilders`).
 */
export function aggregateProjectPLBatch(args: {
  deals: EnhancedDeal[];
  proposalsByDeal: Record<string, ProposalBuilderData | null | undefined>;
  changeOrdersByDeal?: Record<string, ChangeOrder[] | undefined>;
  jobPurchases?: PurchaseRequisition[];
  designerInvoiceJobCash?: DesignerInvoiceJobCash[];
  expenses: LinkedExpenseLite[];
  payments: LinkedPaymentLite[];
  designFeePayments: DesignFeePaymentLite[];
  overridesByDeal: Record<string, DealPLOverrides | null | undefined>;
  pipelineStageNames?: Record<string, string>;
}): ProjectPLRow[] {
  const {
    deals,
    proposalsByDeal,
    changeOrdersByDeal,
    jobPurchases,
    designerInvoiceJobCash,
    expenses,
    payments,
    designFeePayments,
    overridesByDeal,
    pipelineStageNames,
  } = args;
  // Excluded projects don't participate in revenue-share allocation; the remaining
  // active projects share the full overhead pool. This keeps the math honest when
  // legacy / unsalvageable projects are flagged out of the dashboard.
  const totalWonRevenue = deals.reduce((s, d) => {
    if (overridesByDeal[d.id || '']?.excluded) return s;
    return s + (Number(d.value) || 0);
  }, 0);
  const totalOverheadPool = expenses
    .filter(e => e.category === 'overhead' && e.categorizationStatus !== 'pending' && !e.excluded)
    .reduce((s, e) => s + (Number(e.totalAmount) || 0), 0);

  return deals.map(deal =>
    aggregateProjectPL({
      deal,
      proposal: deal.id ? proposalsByDeal[deal.id] : null,
      changeOrders: deal.id ? changeOrdersByDeal?.[deal.id] : undefined,
      jobPurchases,
      designerInvoiceJobCash,
      expenses,
      payments,
      designFeePayments,
      overrides: deal.id ? overridesByDeal[deal.id] : null,
      totalWonRevenue,
      totalOverheadPool,
      pipelineStageName: deal.id ? pipelineStageNames?.[deal.stageId] : undefined,
    }),
  );
}

/** Aggregate KPI strip values for the dashboard top bar. */
export interface ProjectPLKpis {
  activeCount: number;
  totalRevenue: number;
  totalActualCost: number;
  avgGrossMarginPct: number;
  /** Variance only across rows that actually have a budget. `null` when nothing is comparable. */
  avgVarianceVsBudgetPct: number | null;
  budgetedProjectCount: number;
  noProposalCount: number;
}

export function summarizeProjectPL(rows: ProjectPLRow[]): ProjectPLKpis {
  // Excluded rows never count towards KPIs.
  const counted = rows.filter(r => !r.excluded);
  if (counted.length === 0) {
    return {
      activeCount: 0,
      totalRevenue: 0,
      totalActualCost: 0,
      avgGrossMarginPct: 0,
      avgVarianceVsBudgetPct: null,
      budgetedProjectCount: 0,
      noProposalCount: 0,
    };
  }
  const totalRevenue = counted.reduce((s, r) => s + r.revenue, 0);
  const totalActualCost = counted.reduce((s, r) => s + r.actualCost, 0);
  const totalGrossMargin = counted.reduce((s, r) => s + r.grossMargin, 0);
  const avgGrossMarginPct = totalRevenue > 0 ? (totalGrossMargin / totalRevenue) * 100 : 0;
  const budgeted = counted.filter(r => r.budget > 0);
  const totalBudget = budgeted.reduce((s, r) => s + r.budget, 0);
  const totalVariance = budgeted.reduce((s, r) => s + r.varianceVsBudget, 0);
  const avgVarianceVsBudgetPct =
    budgeted.length > 0 && totalBudget > 0 ? (totalVariance / totalBudget) * 100 : null;
  return {
    activeCount: counted.length,
    totalRevenue,
    totalActualCost,
    avgGrossMarginPct,
    avgVarianceVsBudgetPct,
    budgetedProjectCount: budgeted.length,
    noProposalCount: counted.filter(r => !r.hasProposal).length,
  };
}

/** Synced QB payment with no deal and no split — same rule as the transactions modal Unmatched chip. */
export function qbSyncedPaymentIsUnmatched(payment: {
  source?: string;
  excluded?: boolean;
  matchedDealId?: string | null;
  splits?: { amount?: number }[] | null;
}): boolean {
  if (payment.source === 'manual') return false;
  if (payment.excluded) return false;
  const splits = (payment.splits || []).filter(s => Number.isFinite(s.amount));
  return !payment.matchedDealId && splits.length === 0;
}

/** Deal-less expense the financeAgent unmatched queue counts. Pending cards are a separate banner. */
export function expenseIsUnmatchedForPl(expense: {
  excluded?: boolean;
  categorizationStatus?: string | null;
  category?: string | null;
  matchedDealId?: string | null;
  splits?: unknown[] | null;
}): boolean {
  if (expense.excluded) return false;
  if (expense.categorizationStatus === 'pending') return false;
  if (expense.category === 'overhead') return false;
  if (expense.splits && expense.splits.length > 0) return false;
  return !expense.matchedDealId;
}

/** Global QB pool counters (unmatched, overhead) — same full collections as financeAgent plOutstanding. */
export interface ProjectPLGlobalCounters {
  unmatchedExpenseCount: number;
  unmatchedExpenseAmount: number;
  unmatchedPaymentCount: number;
  unmatchedPaymentAmount: number;
  unmatchedTxnCount: number;
  overheadPoolAmount: number;
  pendingCardExpenseCount: number;
  pendingCardExpenseAmount: number;
}

export function computeProjectPLGlobalCounters(
  expenses: LinkedExpenseLite[],
  payments: Array<{
    source?: string;
    excluded?: boolean;
    matchedDealId?: string | null;
    splits?: { amount?: number }[] | null;
    amount?: number;
  }> = [],
): ProjectPLGlobalCounters {
  let unmatchedExpenseCount = 0;
  let unmatchedExpenseAmount = 0;
  let unmatchedPaymentCount = 0;
  let unmatchedPaymentAmount = 0;
  let overheadPoolAmount = 0;
  let pendingCardExpenseCount = 0;
  let pendingCardExpenseAmount = 0;
  for (const e of expenses) {
    const amt = Number(e.totalAmount) || 0;
    if (e.excluded) continue;
    if (e.categorizationStatus === 'pending') {
      pendingCardExpenseCount += 1;
      pendingCardExpenseAmount += amt;
      continue;
    }
    if (expenseIsUnmatchedForPl(e)) {
      unmatchedExpenseCount += 1;
      unmatchedExpenseAmount += amt;
    } else if (e.category === 'overhead') {
      overheadPoolAmount += amt;
    }
  }
  for (const p of payments) {
    if (!qbSyncedPaymentIsUnmatched(p)) continue;
    unmatchedPaymentCount += 1;
    unmatchedPaymentAmount += Number(p.amount) || 0;
  }
  return {
    unmatchedExpenseCount,
    unmatchedExpenseAmount,
    unmatchedPaymentCount,
    unmatchedPaymentAmount,
    unmatchedTxnCount: unmatchedExpenseCount + unmatchedPaymentCount,
    overheadPoolAmount,
    pendingCardExpenseCount,
    pendingCardExpenseAmount,
  };
}
